import { findReadingTypeById } from '@avtechsoftware/ra-javascript-utilities/src/types/ReadingType';
import { OperatorTypeDetails, OperatorTypes } from '@avtechsoftware/ra-javascript-utilities/src/types/OperatorTypes';
import ThresholdRangeTypes from '@avtechsoftware/ra-javascript-utilities/src/types/ThresholdRangeTypes';
import BooleanChannel from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BooleanChannel/BooleanChannel';
import BaseTemperatureChannel from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BaseTemperatureChannel/BaseTemperatureChannel';
import ChannelService
  from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import ChannelType from '@avtechsoftware/ra-javascript-utilities/src/types/ChannelType';
import { getChannelDisplayData, parseChannelKey } from '../../../utility/channelUtils';
import SearchType from '../../../utility/types/SearchType';
import FacetType from '../../../utility/types/facetType';
import ChannelTypeFacet from '../../../classes/facets/ChannelTypeFacet/ChannelTypeFacet';

const createBlankAction = () => ({
  id: `temp_${crypto.randomUUID()}`,
  notificationGroupIdList: [],
  delay: null,
  repeatEnabled: false,
  repeatInterval: null,
  maxReminders: null,
  quietTimeId: null,
  templateId: null,
});

const GRAPHED_CHANNEL_COLORS = [
  '#e91e1e', '#f44336', '#e81b23', '#e53935',
  '#d32f2f', '#c62828', '#bf360c', '#c2185b',
  '#ad1457', '#9c27b0', '#7b1fa2', '#6a1b9a',
  '#4a148c', '#4527a0', '#311b92', '#1a237e',
  '#3f51b5', '#303f9f', '#283593', '#1565c0',
  '#1976d2', '#0d47a1', '#2196f3', '#0277bd',
  '#01579b', '#03a9f4', '#0288d1', '#0092cc',
  '#00bcd4', '#00acc1', '#26c6da', '#009688',
  '#00897b', '#00838f', '#0097a7', '#00695c',
  '#004d40', '#006064', '#4caf50', '#43a047',
  '#388e3c', '#2e7d32', '#1b5e20', '#33691e',
  '#689f38', '#558b2f', '#9ccc65', '#c0ca33',
  '#cddc39', '#ffeb3b', '#fbc02d', '#fdd835',
  '#ffc107', '#ffb300', '#ffa000', '#ff9800',
  '#ff6f00', '#f57f17', '#e65100', '#ff5722',
  '#d84315', '#795548', '#5d4037', '#6d4c41',
  '#8d6e63', '#a1887f', '#bcaaa4', '#d7ccc8',
  '#607d8b', '#455a64', '#37474f', '#263238',
  '#3e2723', '#4e342e', '#efebe9',
];

const stripTemporaryId = (id, prefix = 'temp_') => (
  typeof id === 'string' && id.startsWith(prefix) ? null : id
);

export const getRandomColor = () => GRAPHED_CHANNEL_COLORS[Math.floor(
  Math.random() * GRAPHED_CHANNEL_COLORS.length,
)];
export const mockFetchAlertResponse = {
  id: 98765,

  // === Sensors ===
  channelKeyList: [
    '50:62:55:70:0F:96_50:62:55:70:0F:96_16_0_1_0_1',
  ],
  readingTypeId: 1,

  // === Threshold ===
  thresholdValue: 10,
  thresholdOperator: 1, // >
  thresholdId: -1,
  useReusableThreshold: false,

  // === Actions ===
  actionList: [
    {
      notificationGroupIdList: [1],
      delay: null,
      repeatEnabled: true,
      repeatInterval: 15,
      maxReminders: null,
      quietTimeId: null,
      templateId: null,
    },
  ],

  // === Naming ===
  alertNamePairs: [
    { alertName: 'High Temperature Alert', channelKey: '50:62:55:70:0F:96_50:62:55:70:0F:96_16_0_1_0_1' },
  ],

  // === Tags / Extras ===
  tagIdList: [],
  additionalChannelKeyList: [
  ],
  timeZoneName: 'America/New_York',
};

export const AlertFormSteps = {
  SELECT_SENSORS: 'SELECT_SENSORS',
  DEFINE_THRESHOLD: 'DEFINE_THRESHOLD',
  DEFINE_ACTIONS: 'DEFINE_ACTIONS',
  NAME_ALERT: 'NAME_ALERT',
  REVIEW: 'REVIEW',
};

const generateAlertNamePairs = (input, channels, devices, sensors) => {
  const channelList = Array.isArray(channels) ? channels : [];

  return channelList.map((channelKey) => {
    const data = getChannelDisplayData({ channelKey }, devices, sensors);

    let name = input;
    name = name.replace(/\[DEVICE_NAME]/g, data.deviceName || 'Unknown Device');
    if (name.includes('[COMPONENT_NAME]')) {
      name = name.replace(/\[COMPONENT_NAME]/g, data.maxComponentLabel || '');
    }
    name = name.replace(/\[SENSOR_NAME]/g, data.sensorName || 'Unknown Sensor');
    name = name.replace(/\[CHANNEL_TYPE]/g, data.channelName || 'Unknown Channel');
    name = name.replace(/\[SENSOR_PORT]/g, data.port || '');

    return { channelKey, alertName: name };
  });
};

const formatChannelDetails = (data) => {
  const deviceName = data && data.deviceName ? data.deviceName : 'Unknown Device';
  const port = data && data.port ? data.port : '';
  const sensorName = data && data.sensorName ? data.sensorName : 'Unknown Sensor';
  const groupName = data && data.groupName ? data.groupName : 'Unknown Sensor Name';
  const channelName = data && data.channelName ? data.channelName : 'Unknown Channel Name';

  return `("${deviceName}" - ${port} - "${sensorName}" - ${groupName} > ${channelName})`;
};

const getAlertRowDisplayData = (channelKey, alertName, deviceList, sensorMap) => {
  const displayData = channelKey
    ? getChannelDisplayData({ channelKey }, deviceList, sensorMap)
    : null;

  return {
    key: channelKey || alertName,
    alertName: alertName || '',
    details: formatChannelDetails(displayData),
  };
};

const getThresholdSource = (sensorsData, thresholdData, existingThresholdRange) => {
  if (existingThresholdRange) {
    return {
      thresholdName: existingThresholdRange.name,
      readingType: existingThresholdRange.readingType,
      thresholdOperator: existingThresholdRange.operatorId,
      thresholdValue: existingThresholdRange.value,
    };
  }

  return {
    thresholdName: 'Custom Threshold',
    readingType: sensorsData ? sensorsData.readingType : null,
    thresholdOperator: thresholdData ? thresholdData.thresholdOperator : null,
    thresholdValue: thresholdData ? thresholdData.thresholdValue : null,
  };
};

const formatThresholdDetails = (
  readingType,
  thresholdOperator,
  thresholdValue,
  scaleOptions = {},
  channelImpl = null,
) => {
  const readingTypeMeta = findReadingTypeById(readingType);

  const readingTypeDisplay = readingTypeMeta && readingTypeMeta.displayName
    ? readingTypeMeta.displayName
    : 'Default';

  const operatorDetails = thresholdOperator ? OperatorTypeDetails[thresholdOperator] : null;
  const operatorDisplay = operatorDetails && operatorDetails.symbol
    ? operatorDetails.symbol
    : String(thresholdOperator);

  const isBooleanChannel = channelImpl && channelImpl instanceof BooleanChannel;
  const canUseChannelDisplay = channelImpl && typeof channelImpl.getDisplayValue === 'function';

  const userDisplayValue = (canUseChannelDisplay)
    ? channelImpl.getDisplayValue(thresholdValue)
    : thresholdValue;

  if (isBooleanChannel) {
    const boolDisplay = (userDisplayValue === null || typeof userDisplayValue === 'undefined')
      ? ''
      : String(userDisplayValue);

    return `(${['is', boolDisplay].filter(Boolean).join(' ')})`;
  }

  const units = (readingTypeMeta && typeof readingTypeMeta.getUnits === 'function')
    ? readingTypeMeta.getUnits(scaleOptions)
    : '';

  const valueWithUnits = (userDisplayValue === null || typeof userDisplayValue === 'undefined')
    ? ''
    : String(userDisplayValue) + (units ? ` ${units}` : '');

  return `(${[readingTypeDisplay, operatorDisplay, valueWithUnits].filter(Boolean).join(' ')})`;
};

// Helper function to convert symbol to OperatorTypes
const getOperatorTypeFromSymbol = (symbol) => {
  const entry = Object.entries(OperatorTypes).find(
    ([, value]) => {
      const details = OperatorTypeDetails[value];
      return details && details.symbol === symbol;
    },
  );
  return entry ? entry[1] : null;
};

// Helper function to convert OperatorTypes ID back to symbol
const getSymbolFromOperatorType = (operatorType) => {
  const details = OperatorTypeDetails[operatorType];
  return details ? details.symbol : null;
};

const mapStateToCreateAlertJson = (
  sensorsData,
  thresholdData,
  actionsData,
  nameAlertData,
  scalePreferences,
) => {
  const {
    alertNamePairs, alertTags, additionalSensors, timeZoneName,
  } = nameAlertData || {};
  const {
    thresholdValue, thresholdOperator, thresholdId, useSharedThreshold,
  } = thresholdData || {};
  const {
    readingType,
    channelType,
    selectedChannels,
  } = sensorsData || {};

  const { actions } = actionsData || {};
  const channelKeyList = [selectedChannels[0]];
  const channelImpl = ChannelService.getChannelFromChannelKey(channelKeyList[0]);
  const conversionScale = channelImpl instanceof BaseTemperatureChannel
    ? scalePreferences.temperatureScale
    : scalePreferences.airSpeedScale;
  const scaledValue = channelImpl
    ? channelImpl.fromDisplayValue(thresholdValue,
      {
        scale: conversionScale,
        airSpeedScale: scalePreferences.airSpeedScale,
      })
    : thresholdValue;

  return {
    alertNamePairs,
    thresholdValue: parseFloat(scaledValue),
    thresholdOperator: Number.isNaN(Number(thresholdOperator))
      ? getOperatorTypeFromSymbol(thresholdOperator)
      : parseInt(thresholdOperator, 10),
    useReusableThreshold: useSharedThreshold,
    thresholdId: parseInt(thresholdId, 10),
    channelTypeId: channelType,
    readingTypeId: readingType,
    actionList: (Array.isArray(actions) ? actions : []).map((action) => {
      const groupIds = action && action.notificationGroupIdList
        ? action.notificationGroupIdList
        : [];
      return {
        notificationGroupIdList: groupIds.filter((id) => id !== null && id !== undefined),
        delay: action.delay || 0,
        repeatEnabled: !!action.repeatEnabled,
        repeatInterval: action.repeatInterval || 0,
        maxReminders: action.maxRepeatEnabled
          ? (action.maxReminders || 1)
          : null,
        quietTimeId: action.quietTimeId || null,
        templateId: action.templateId || null,
      };
    }),
    tagIdList: (Array.isArray(alertTags) ? alertTags : [])
      .map((tag) => (tag && tag.id !== undefined ? tag.id : null))
      .filter((id) => id !== null && id !== undefined),
    additionalChannelKeyList: (Array.isArray(additionalSensors) ? additionalSensors : [])
      .filter((key) => !!key),
    timeZoneName: timeZoneName || 'America/Detroit',
  };
};

const mapAlertResponseToFormSteps = (alertData, scalePreferences = {}) => {
  if (!alertData) return {};
  const {
    alert,
    thresholdRange,
    channelKey,
    alertActions,
    tags,
    additionalChannelKeys,
  } = alertData;

  const { temperatureScale = 'C' } = scalePreferences;
  const { airSpeedScale = 'm/s' } = scalePreferences;

  const channelKeyList = [channelKey];
  const readingTypeId = thresholdRange.readingType;
  const {
    channelGroupType,
    channelIndex,
    channelType,
  } = parseChannelKey(channelKeyList[0]);

  const channelImpl = ChannelService.getChannelFromChannelKey(channelKeyList[0]);
  const conversionScale = channelImpl instanceof BaseTemperatureChannel
    ? temperatureScale
    : airSpeedScale;
  const thresholdValue = channelImpl && typeof channelImpl.getDisplayValue === 'function'
    ? channelImpl.getDisplayValue(thresholdRange.value, {
      scale: conversionScale,
      airSpeedScale,
    })
    : thresholdRange.value;

  const thresholdOperator = thresholdRange.operatorId;
  const thresholdId = thresholdRange.id;
  const thresholdTypeId = thresholdRange.typeId;
  const useReusableThreshold = (thresholdTypeId !== 3);

  const actionList = alertActions || [];

  const alertNamePairs = [{ alertName: alert.description, channelKey }];
  const tagIdList = tags || [];
  const additionalChannelKeyList = additionalChannelKeys || [];
  const { timezone: timeZoneName } = alert;
  return {
    [AlertFormSteps.SELECT_SENSORS]: {
      selectedChannels: channelKeyList,
      graphedChannels: channelKeyList,
      graphedChannelColors: { [channelKeyList[0]]: getRandomColor() },
      graphedAlertConfig: {
        threshold: null,
        channelKeyObject: {
          channelGroupType,
          channelIndex,
          channelType,
        },
        customUnits: '',
      },
      readingType: readingTypeId,
      channelType,
      completed: channelKeyList.length > 0,
    },

    [AlertFormSteps.DEFINE_THRESHOLD]: {
      thresholdValue,
      thresholdOperator,
      thresholdId,
      useSharedThreshold: !!useReusableThreshold,
      readingType: readingTypeId,
      readingTypeId,
      channelType,
      completed:
        thresholdValue !== null
        && thresholdValue !== undefined
        && thresholdOperator !== null
        && thresholdOperator !== undefined,
    },

    [AlertFormSteps.DEFINE_ACTIONS]: {
      actions: actionList.map((action) => ({
        id: action.id,
        notificationGroupIdList:
          action && action.notificationGroupIds
            ? action.notificationGroupIds
            : [],
        delay: action && action.delay ? action.delay : 0,
        repeatEnabled: (action.repeatInterval || 0) > 0,
        repeatInterval: action.repeatInterval || 0,
        maxRepeatEnabled: !!(action && action.maxReminders > 0),
        maxReminders: action && action.maxReminders
          ? action.maxReminders
          : 0,
        quietTimeId: action && action.quietTimeId
          ? action.quietTimeId
          : null,
        templateId: action && action.templateId
          ? action.templateId
          : null,
      })),
      completed: actionList.length > 0,
    },

    [AlertFormSteps.NAME_ALERT]: {
      alertNamePairs,
      alertNameInput:
        alertNamePairs.length > 0 && alertNamePairs[0]
          ? alertNamePairs[0].alertName
          : '',
      alertTags: tagIdList.map((id) => ({ id })),
      additionalSensors: additionalChannelKeyList,
      timeZoneName,
      completed: alertNamePairs.length > 0,
    },
  };
};

const mapAlertResponseToCloneFormSteps = (alertData, scalePreferences = {}) => {
  const mapped = mapAlertResponseToFormSteps(alertData, scalePreferences);

  if (!mapped) return mapped;

  // ---- NAME STEP ----
  if (mapped[AlertFormSteps.NAME_ALERT]) {
    const nameStep = mapped[AlertFormSteps.NAME_ALERT];

    const clonedPairs = (nameStep.alertNamePairs || []).map((pair) => ({
      ...pair,
      alertName: pair.alertName
        ? `${pair.alertName} (Clone)`
        : '',
    }));

    mapped[AlertFormSteps.NAME_ALERT] = {
      ...nameStep,
      alertNamePairs: clonedPairs,
      alertNameInput: clonedPairs.length > 0 ? clonedPairs[0].alertName : '',
      completed: clonedPairs.length > 0,
    };
  }

  // ---- THRESHOLD STEP ----
  // Only clear thresholdId when the source thresholdRange is independent
  if (mapped[AlertFormSteps.DEFINE_THRESHOLD]) {
    const sourceThresholdTypeId = alertData
    && alertData.thresholdRange
    && alertData.thresholdRange.typeId
      ? alertData.thresholdRange.typeId
      : null;

    mapped[AlertFormSteps.DEFINE_THRESHOLD] = {
      ...mapped[AlertFormSteps.DEFINE_THRESHOLD],
      thresholdId: sourceThresholdTypeId === ThresholdRangeTypes.ALERT
        ? -1
        : mapped[AlertFormSteps.DEFINE_THRESHOLD].thresholdId,
    };
  }

  // ---- ACTIONS STEP ----
  // Reset UI-only IDs to avoid collisions
  if (mapped[AlertFormSteps.DEFINE_ACTIONS]) {
    mapped[AlertFormSteps.DEFINE_ACTIONS] = {
      ...mapped[AlertFormSteps.DEFINE_ACTIONS],
      actions: (mapped[AlertFormSteps.DEFINE_ACTIONS].actions || [])
        .map((action, index) => ({
          ...action,
          id: index,
        })),
    };
  }

  return mapped;
};

export const mapEditFormStateToJson = (
  sensorsData,
  thresholdData,
  actionsData,
  nameAlertData,
  scalePreferences = {},
) => {
  const {
    alertNamePairs,
    alertTags,
    additionalSensors,
    timeZoneName,
  } = nameAlertData || {};

  const { temperatureScale = 'C', airSpeedScale = 'm/s' } = scalePreferences;

  const {
    thresholdValue,
    thresholdOperator,
    thresholdId,
    useSharedThreshold,
    readingType: thresholdReadingType,
    channelType: thresholdChannelType,
  } = thresholdData || {};

  const {
    readingType: sensorReadingType,
    channelType: sensorChannelType,
    selectedChannels,
  } = sensorsData || {};

  const { actions } = actionsData || {};

  const finalChannelType = sensorChannelType || thresholdChannelType;
  const finalReadingType = sensorReadingType || thresholdReadingType;

  const channelImpl = ChannelService.getChannelFromChannelKey(selectedChannels[0]);
  const conversionScale = channelImpl instanceof BaseTemperatureChannel
    ? temperatureScale
    : airSpeedScale;
  const scaledValue = channelImpl
    ? channelImpl.fromDisplayValue(thresholdValue, { scale: conversionScale, airSpeedScale })
    : thresholdValue;

  const parsedOperator = Number.isNaN(Number(thresholdOperator))
    ? getOperatorTypeFromSymbol(thresholdOperator)
    : parseInt(thresholdOperator, 10);

  let newThresholdId = parseInt(thresholdId, 10) || null;
  if (thresholdId === -1) newThresholdId = null;
  return {
    alertName: alertNamePairs && alertNamePairs[0] ? alertNamePairs[0].alertName : '',
    tagIdList: (alertTags || [])
      .map((tag) => (tag && tag.id !== undefined ? tag.id : tag))
      .filter((id) => id !== null),
    additionalChannelKeyList: additionalSensors || [],
    channelKey: selectedChannels[0],
    timeZoneName: timeZoneName || 'America/New_York',
    thresholdRange: {
      id: newThresholdId,
      operatorId: parsedOperator,
      value: parseFloat(scaledValue),
      channelTypeId: finalChannelType,
      typeId: useSharedThreshold ? ThresholdRangeTypes.CUSTOM : ThresholdRangeTypes.ALERT,
      readingTypeId: finalReadingType,
    },
    actionList: (Array.isArray(actions) ? actions : []).map((action) => ({
      id: stripTemporaryId(action.id),
      notificationGroupIdList: action.notificationGroupIdList || [],
      delay: action.delay || 0,
      repeatInterval: action.repeatInterval || 0,
      maxReminders: action.maxRepeatEnabled
        ? (action.maxReminders || 1)
        : null,
      quietTimeId: action.quietTimeId || null,
      templateId: action.templateId || null,
      type: 'NOTIFICATION',
    })),
  };
};

const normalizeThresholdOperatorToSymbol = (operator) => {
  if (operator === null || operator === undefined) {
    // Null and undefined are invalid
    return null;
  }

  if (typeof operator === 'number') {
    if (Number.isNaN(operator)) {
      // Handle NaN explicitly
      return null;
    }
    return getSymbolFromOperatorType(operator); // Process valid numbers
  }

  if (typeof operator === 'string') {
    if (operator.trim() === '') {
      // Handle empty or whitespace-only strings as invalid
      return null;
    }

    // Check if the string can be treated as a number
    const parsedNumber = Number(operator);
    if (!Number.isNaN(parsedNumber)) {
      return getSymbolFromOperatorType(parsedNumber); // Process stringified numbers
    }

    return operator; // Return valid non-empty strings
  }

  // Handle all other unexpected types (objects, arrays, functions, booleans, etc.)
  return null;
};

const getAlertGraphThresholdConfig = (
  useSharedThreshold = false,
  thresholdId = -1,
  sharedThresholds,
  thresholdValue,
  thresholdOperator,
) => {
  if (useSharedThreshold) {
    // Get value and operator from shared threshold
    const sharedThresholdId = thresholdId || -1;
    const sharedThreshold = sharedThresholds.find(
      (threshold) => threshold.id === sharedThresholdId,
    );
    if (sharedThreshold) {
      return {
        value: sharedThreshold.value,
        operator: normalizeThresholdOperatorToSymbol(sharedThreshold.operatorId),
      };
    }
    return { value: null, operator: null };
  }

  return {
    value: thresholdValue,
    operator:
        thresholdOperator !== null
          ? normalizeThresholdOperatorToSymbol(thresholdOperator)
          : null,
  };
};

const getIncompleteSteps = (alertForm) => {
  const stepKeys = Object.values(AlertFormSteps);
  return stepKeys.filter((step) => alertForm[step] && alertForm[step].completed !== true);
};

const buildChannelTypeFiltersFromChannelKey = (
  channelKey,
  operator = OperatorTypes.EQUAL_TO_VALUE,
) => {
  const facet = new ChannelTypeFacet();
  const options = facet.getValueSelectOptions();
  const { channelGroupType, channelType, channelIndex } = parseChannelKey(channelKey);
  let channelName = ChannelService
    .getChannelGroupChannel(channelGroupType, channelIndex, channelType).displayName;
  if (channelType === ChannelType.SWITCH) {
    channelName = 'Switch';
  }
  const group = options.find((opt) => opt.label === channelName);
  if (!group) return [];
  return group.value.map((key) => ({
    id: SearchType.CHANNEL,
    facetId: FacetType.CHANNEL_TYPE,
    operatorId: operator,
    value1: key,
  }));
};

export {
  generateAlertNamePairs,
  formatChannelDetails,
  getAlertRowDisplayData,
  getThresholdSource,
  formatThresholdDetails,
  mapStateToCreateAlertJson,
  mapAlertResponseToFormSteps,
  mapAlertResponseToCloneFormSteps,
  getOperatorTypeFromSymbol,
  getSymbolFromOperatorType,
  createBlankAction,
  normalizeThresholdOperatorToSymbol,
  getAlertGraphThresholdConfig,
  getIncompleteSteps,
  buildChannelTypeFiltersFromChannelKey,
};
